import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import logo from "@/assets/logo-sm.webp";
import { DesktopNav } from "@/components/header/DesktopNav";
import { MobileNav } from "@/components/header/MobileNav";
import { LanguageSwitcher } from "@/components/header/LanguageSwitcher";
import { NAV, BN_NAV } from "@/components/header/nav-config";


export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isBn = pathname === "/bn" || pathname.startsWith("/bn/");
  const navItems = isBn ? BN_NAV : NAV;

  // Close the menu after any navigation (including back/forward) and on Escape.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-app-header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="container-x">
        <div
          className={`flex items-center justify-between rounded-full px-5 py-3 transition-all ${
            scrolled ? "glass shadow-[var(--shadow-elegant)]" : ""
          }`}
        >
          <Link to={isBn ? "/bn" : "/"} className="flex items-center group shrink-0 min-w-0" aria-label="Freelancer Tamal — home">
            <img
              src={logo}
              alt="Freelancer Tamal"
              width={430}
              height={160}
              decoding="async"
              fetchPriority="high"
              className={`w-auto max-w-[55vw] sm:max-w-none object-contain group-hover:opacity-90 transition-all duration-300 ${
                scrolled
                  ? "h-10 sm:h-11 md:h-12"
                  : "h-12 sm:h-14 md:h-16 xl:h-14"
              }`}
            />
          </Link>

          <DesktopNav items={navItems} />

          {/* lg shows only the booking button beside the menu toggle; the full nav,
              language switch and audit link need xl width to fit on one line. */}
          <div className="hidden lg:flex items-center gap-2 shrink-0 ml-auto xl:ml-0">
            <div className="hidden xl:block">
              <LanguageSwitcher />
            </div>

            <a
              href="https://tool.freelancertamal.com/"
              target="_blank"
              rel="noopener"
              className="hidden xl:inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-full border border-primary/60 text-primary px-4 py-2.5 text-sm font-medium hover:bg-primary/10 transition"
            >
              Free Audit
            </a>
            <Link
              to={isBn ? "/bn/book" : "/book"}
              className="inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:shadow-[var(--shadow-glow)] transition"
            >
              {isBn ? "কল বুক করুন" : "Book a call"}
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden p-2 ml-1 text-foreground"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {open && <MobileNav onClose={() => setOpen(false)} items={navItems} isBn={isBn} />}
      </div>
    </header>
  );
}
